import { ArrowLeft, BookOpen, ChevronRight, Database, Flag, ListChecks } from 'lucide-react'
import { FLAGS } from '@atbash-labs/ontology-flags'
import {
  isDebugModeConfigEnabled,
  useListFlaggedNodesQuery
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { useFlag } from '../../components/flags/useFlag'
import { Card } from '../../components/ui/card'
import { Separator } from '../../components/ui/separator'
import { DebugModeToggle } from './components/DebugModeCard'
import { FeatureFlagsCard } from './components/FeatureFlagsCard'
import { ModelSelectionCard } from './components/ModelSelectionCard'
import { DatabasesTab, databasesToolFromPanel } from './DatabasesTab'
import { FlaggedNodesCard } from './graph-properties/FlaggedNodesCard'
import { ReviewCard } from './graph-properties/ReviewCard'
import { ConditionVocabularyCard } from './condition-vocabulary/ConditionVocabularyCard'
import type { AdvancedPanel } from './settings-nav'

function PanelHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="ghost" size="icon-sm" onClick={onBack} aria-label="Back">
        <ArrowLeft className="size-4" />
      </Button>
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
    </div>
  )
}

function AdvancedHub({
  debugMode,
  onOpen
}: {
  debugMode: boolean
  onOpen: (panel: Exclude<AdvancedPanel, 'hub'>) => void
}) {
  const debugToggleEnabled = isDebugModeConfigEnabled()
  const flaggedQuery = useListFlaggedNodesQuery()
  const flaggedCount = flaggedQuery.data?.length ?? 0
  const showDatabases = debugToggleEnabled && debugMode
  const sightingsEnabled = useFlag(FLAGS.RELEASE_SIGHTING_PLANNER)

  return (
    <div className="flex flex-col gap-4">
      <DebugModeToggle />
      <FeatureFlagsCard />

      <Card className="gap-0 overflow-hidden bg-muted/40 py-0 shadow-sm">
        <div className="px-4 py-2.5 text-11 font-semibold tracking-[0.08em] text-ink-3 uppercase">
          Knowledge graph
        </div>

        <Separator />

        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start gap-3 rounded-none bg-card px-4 py-2.5"
          onClick={() => onOpen('flagged')}
        >
          <Flag className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium text-ink-2">Flagged nodes</div>
            <div className="text-xs font-normal text-ink-3">
              Marked not valid — agents avoid citing them.
            </div>
          </div>
          {flaggedCount > 0 ? (
            <Badge variant="secondary" className="bg-st-down-bg text-st-down">
              {flaggedCount}
            </Badge>
          ) : null}
          <ChevronRight className="size-4 shrink-0 text-ink-3" />
        </Button>

        <Separator />

        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start gap-3 rounded-none bg-card px-4 py-2.5"
          onClick={() => onOpen('review')}
        >
          <ListChecks className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium text-ink-2">Review</div>
            <div className="text-xs font-normal text-ink-3">
              Needs-review and low-confidence nodes and edges.
            </div>
          </div>
          <ChevronRight className="size-4 shrink-0 text-ink-3" />
        </Button>

        {sightingsEnabled ? (
          <>
            <Separator />
            <Button
              type="button"
              variant="ghost"
              className="h-auto w-full justify-start gap-3 rounded-none bg-card px-4 py-2.5"
              onClick={() => onOpen('condition-vocabulary')}
            >
              <BookOpen className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
              <div className="min-w-0 flex-1 text-left">
                <div className="text-sm font-medium text-ink-2">Condition vocabulary</div>
                <div className="text-xs font-normal text-ink-3">
                  Applicability, source, and check usefulness.
                </div>
              </div>
              <ChevronRight className="size-4 shrink-0 text-ink-3" />
            </Button>
          </>
        ) : null}
      </Card>

      <ModelSelectionCard />

      {showDatabases ? (
        <Card className="gap-0 overflow-hidden bg-muted/40 py-0 shadow-sm">
          <div className="px-4 py-2.5 text-11 font-semibold tracking-[0.08em] text-ink-3 uppercase">
            Databases
          </div>

          <Separator />

          <Button
            type="button"
            variant="ghost"
            className="h-auto w-full justify-start gap-3 rounded-none bg-card px-4 py-2.5"
            onClick={() => onOpen('databases')}
          >
            <Database className="size-4 shrink-0 text-ink-3" strokeWidth={1.75} />
            <div className="min-w-0 flex-1 text-left">
              <div className="text-sm font-medium text-ink-2">Databases</div>
              <div className="text-xs font-normal text-ink-3">
                Neo4j/Postgres backup, SQL, Cypher writes, clear, and FactorySim load.
              </div>
            </div>
            <Badge variant="secondary">debug only</Badge>
            <ChevronRight className="size-4 shrink-0 text-ink-3" />
          </Button>
        </Card>
      ) : null}
    </div>
  )
}

function isDatabasesPanel(panel: AdvancedPanel): boolean {
  return panel === 'databases' || panel === 'postgres-query' || panel === 'cypher-write'
}

export function AdvancedSection({
  panel,
  debugMode,
  onPanelChange
}: {
  panel: AdvancedPanel
  debugMode: boolean
  onPanelChange: (panel: AdvancedPanel) => void
}) {
  const sightingsEnabled = useFlag(FLAGS.RELEASE_SIGHTING_PLANNER)
  if (panel === 'flagged') {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Flagged nodes" onBack={() => onPanelChange('hub')} />
        <FlaggedNodesCard />
      </div>
    )
  }

  if (panel === 'review' || panel === 'low-confidence') {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Review" onBack={() => onPanelChange('hub')} />
        <ReviewCard defaultView={panel === 'low-confidence' ? 'low-confidence' : 'needs-review'} />
      </div>
    )
  }

  if (panel === 'condition-vocabulary' && sightingsEnabled) {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Condition vocabulary" onBack={() => onPanelChange('hub')} />
        <ConditionVocabularyCard />
      </div>
    )
  }

  if (isDatabasesPanel(panel) && debugMode) {
    return (
      <div className="flex flex-col gap-4">
        <PanelHeader title="Databases" onBack={() => onPanelChange('hub')} />
        <DatabasesTab initialTool={databasesToolFromPanel(panel) ?? 'neo4j-download'} />
      </div>
    )
  }

  return <AdvancedHub debugMode={debugMode} onOpen={onPanelChange} />
}
